import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CardTitle from "@saleor/components/CardTitle";
import { DateTime } from "@saleor/components/Date";
import Money from "@saleor/components/Money";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellAvatar, {
  AVATAR_MARGIN
} from "@saleor/components/TableCellAvatar";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe } from "../../../misc";
import { OrderDetails_order_lines } from "../../types/OrderDetails";

const useStyles = makeStyles(
  {
    root: {
      lineHeight: 1.9,
      width: "100%"
    },
    clickableRow: {
      cursor: "pointer"
    },
    colName: {
      paddingLeft: 0,
      width: "auto"
    },
    colNameLabel: {
      marginLeft: AVATAR_MARGIN
    },
    colPrice: {
      textAlign: "right",
      width: 120
    },
    colQuantity: {
      textAlign: "center",
      width: 120
    },
    colSku: {
      textAlign: "right",
      textOverflow: "ellipsis",
      width: 120
    },
    colTotal: {
      textAlign: "right",
      width: 120
    },
    colImg: {
      width: 120
    },
    statusBar: {
      paddingTop: 0
    },
    fileField: {
      display: "none"
    },
    table: {
      tableLayout: "fixed"
    }
  },
  { name: "OrderUnfulfilledItems" }
);

interface OrderUnfulfilledItemsProps {
  canFulfill: boolean;
  lines: OrderDetails_order_lines[];
  onFulfill: () => void;
  updateOrderOptometryImage: (image: any, id: string) => void;
  updateLineOptometryImage: (image: any, lineId: string) => void;
  orderId: string;
}

const OrderUnfulfilledItems: React.FC<OrderUnfulfilledItemsProps> = props => {
  const {
    canFulfill,
    lines,
    onFulfill,
    orderId,
    updateOrderOptometryImage,
    updateLineOptometryImage
  } = props;
  lines.map(line => {
    line.customizedData = line.customizedData.replaceAll(": null", ': "None"');
  });
  const classes = useStyles(props);

  const strSPH = "SPH";
  const strCYL = "CYL";
  const strAXIS = "AXIS";
  const strADD = "ADD";
  const intl = useIntl();

  const uploadall = React.useRef(null);
  return (
    <Card>
      <CardTitle
        title={
          <StatusLabel
            label={intl.formatMessage(
              {
                defaultMessage: "Unfulfilled ({quantity})",
                description: "section header"
              },
              {
                quantity: lines
                  .map(line => line.quantity - line.quantityFulfilled)
                  .reduce((prev, curr) => prev + curr, 0)
              }
            )}
            status="error"
          />
        }
      />
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.colName}>
              <span className={classes.colNameLabel}>
                <FormattedMessage
                  defaultMessage="Product"
                  description="product name"
                />
              </span>
            </TableCell>
            <TableCell className={classes.colSku}>
              <FormattedMessage
                defaultMessage="SKU"
                description="ordered product sku"
              />
            </TableCell>
            <TableCell className={classes.colQuantity}>
              <FormattedMessage
                defaultMessage="Quantity"
                description="ordered products"
              />
            </TableCell>
            <TableCell className={classes.colPrice}>
              <FormattedMessage
                defaultMessage="Price"
                description="product unit price"
              />
            </TableCell>
            <TableCell className={classes.colTotal}>
              <FormattedMessage
                defaultMessage="Total"
                description="order line total price"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map(line => {
            const upload = React.useRef(null);
            return (
              <>
                <TableRow
                  className={!!line ? classes.clickableRow : undefined}
                  hover={!!line}
                  key={maybe(() => line.id)}
                >
                  <TableCellAvatar
                    className={classes.colName}
                    thumbnail={maybe(() => line.thumbnail.url)}
                  >
                    {line ? (
                      maybe(() => line.productName + "  " + line.variantName)
                    ) : (
                      <Skeleton />
                    )}
                    <Button
                      onClick={() => upload.current.click()}
                      variant="text"
                      color="primary"
                      data-test="button-upload-image"
                    >
                      {intl.formatMessage({
                        defaultMessage: "Upload",
                        description: "section header"
                      })}
                    </Button>
                    <input
                      className={classes.fileField}
                      id={"fileUpload" + line.id}
                      onChange={event =>
                        updateLineOptometryImage(event.target.files[0], line.id)
                      }
                      multiple
                      type="file"
                      ref={upload}
                      accept="image/*"
                    />
                  </TableCellAvatar>
                  <TableCell className={classes.colSku}>
                    {line?.productSku || <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.colQuantity}>
                    {maybe(() => line.quantity - line.quantityFulfilled) || (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colPrice}>
                    {maybe(() => line.unitPrice.net) ? (
                      <Money money={line.unitPrice.net} />
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.colTotal}>
                    {maybe(
                      () =>
                        (line.quantity - line.quantityFulfilled) *
                        line.unitPrice.net.amount
                    ) ? (
                      <Money
                        money={{
                          amount:
                            (line.quantity - line.quantityFulfilled) *
                            line.unitPrice.net.amount,
                          currency: line.unitPrice.net.currency
                        }}
                      />
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>

                {(line?.customizedData !== "{}" ||
                  line?.extrachargeData !== "{}") && (
                  <TableRow>
                    {/* 个人定制 */}
                    <TableCell colSpan={2}>
                      {line?.customizedData &&
                        JSON.stringify(line?.customizedData) !== "{}" && (
                          <table className={classes.root}>
                            {Object.keys(JSON.parse(line?.customizedData))
                              ?.length > 2 && (
                              <tr>
                                <td>{""}</td>
                                <td>{strSPH}</td>
                                <td>{strCYL}</td>
                                <td>{strAXIS}</td>
                                <td>{strADD}</td>
                              </tr>
                            )}
                            {Object.keys(JSON.parse(line.customizedData)).map(
                              key => (
                                <tr>
                                  <td>{key}</td>
                                  {JSON.parse(line.customizedData)[key].length >
                                    0 && (
                                    <>
                                      {key === "Upload a photo" ? (
                                        <td>
                                          <img
                                            className={classes.colImg}
                                            src={
                                              JSON.parse(line.customizedData)[
                                                key
                                              ]
                                            }
                                          />
                                        </td>
                                      ) : (
                                        <td>
                                          <span>
                                            {
                                              JSON.parse(line.customizedData)[
                                                key
                                              ]
                                            }
                                          </span>
                                        </td>
                                      )}
                                    </>
                                  )}
                                  {Object.keys(JSON.parse(line?.customizedData))
                                    ?.length > 2 && (
                                    <>
                                      <td>
                                        {
                                          JSON.parse(line.customizedData)[key][
                                            strSPH
                                          ]
                                        }
                                      </td>
                                      <td>
                                        {
                                          JSON.parse(line.customizedData)[key][
                                            strCYL
                                          ]
                                        }
                                      </td>
                                      <td>
                                        {
                                          JSON.parse(line.customizedData)[key][
                                            strAXIS
                                          ]
                                        }
                                      </td>
                                      <td>
                                        {
                                          JSON.parse(line.customizedData)[key][
                                            strADD
                                          ]
                                        }
                                      </td>
                                    </>
                                  )}
                                </tr>
                              )
                            )}
                            {line?.optometryImage && (
                              <tr>
                                <td>{"Optometry Image"}</td>
                                <td>
                                  <img
                                    className={classes.colImg}
                                    src={line?.optometryImage}
                                  />
                                </td>
                              </tr>
                            )}
                          </table>
                        )}
                    </TableCell>

                    {/* 定制收费 */}
                    <TableCell colSpan={3}>
                      {line?.extrachargeData &&
                        JSON.stringify(line?.extrachargeData) !== "{}" && (
                          <table className={classes.root}>
                            {Object.keys(JSON.parse(line.extrachargeData)).map(
                              key => (
                                <>
                                  <tr>
                                    <td>{key} </td>
                                    <td>
                                      <Money
                                        money={{
                                          amount: JSON.parse(
                                            line.extrachargeData
                                          )[key].price,
                                          currency: JSON.parse(
                                            line.extrachargeData
                                          )[key].currency
                                        }}
                                      />
                                    </td>
                                  </tr>
                                </>
                              )
                            )}
                          </table>
                        )}
                    </TableCell>
                  </TableRow>
                )}

                {line?.preOrder && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      Pre Order: <DateTime date={line?.preOrder} />
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </ResponsiveTable>

      <CardActions>
        <Button
          onClick={() => uploadall.current.click()}
          variant="text"
          color="primary"
          data-test="button-upload-image"
        >
          {intl.formatMessage({
            defaultMessage: "Upload All",
            description: "section header"
          })}
        </Button>
        <input
          className={classes.fileField}
          id="fileUploadAll"
          onChange={event =>
            updateOrderOptometryImage(event.target.files[0], orderId)
          }
          multiple
          type="file"
          ref={uploadall}
          accept="image/*"
        />
        {canFulfill && (
          <Button variant="text" color="primary" onClick={onFulfill}>
            <FormattedMessage defaultMessage="Fulfill" description="button" />
          </Button>
        )}
      </CardActions>
    </Card>
  );
};
OrderUnfulfilledItems.displayName = "OrderUnfulfilledItems";
export default OrderUnfulfilledItems;
